// InterviewsList.js
import React, { useState, useEffect, lazy, Suspense } from 'react';
import axios from 'axios';
import './InterviewsList.css';
import API_BASE_URL from '../config';
import { useNavigate } from 'react-router-dom';
import AuthModal from './HRAuthPage'; // Add this import

import { 
  FaBullseye,
  FaSyncAlt, 
  FaSmile,
  FaCalendarAlt, 
  FaUsers, 
  FaClock,  
  FaChartBar,
  FaPartyHorn,
  FaVideo,
  FaMapMarkerAlt,
  FaCheck,
  FaTimes,
  FaPen,
  FaFileAlt,
  FaRocket,
  FaStar
 } from "react-icons/fa";



// Lazy load the OfferLetter component
const OfferLetter = lazy(() => import('./OfferLetter'));

const InterviewsList = () => {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState('');
  const [view, setView] = useState('upcoming');
  const [error, setError] = useState('');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalType, setAuthModalType] = useState('login');
  const [onboardingCandidate, setOnboardingCandidate] = useState(null);

  const [allInterviews, setAllInterviews] = useState([]);

  const [feedback, setFeedback] = useState({
    rating: 5,
    comments: '',
    strengths: [''],
    areasForImprovement: [''],
    decision: 'Move to Next Round'
  });

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleData, setScheduleData] = useState({
    scheduledDate: '',
    scheduledTime: '',
    interviewType: 'Technical',
    interviewMode: 'Video Call',
    interviewers: [''],
    meetingLink: '',
    location: '',
    notes: '',
    duration: 60
  });

  const [showAnswersModal, setShowAnswersModal] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [candidateAnswers, setCandidateAnswers] = useState([]);
  const [answersLoading, setAnswersLoading] = useState(false);
  const [detailedSubmission, setDetailedSubmission] = useState(null);
  const [showDetailedView, setShowDetailedView] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Offer Letter Popup State
  const [showOfferLetter, setShowOfferLetter] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  const navigate = useNavigate();

  const handleRecruit = (interview) => {
    console.log('Start Onboarding clicked for:', interview.candidateName);

    // Prepare candidate data to pass to HR login page
    const candidateData = {
      name: interview.candidateName,
      email: interview.candidateEmail,
      position: interview.position,
      department: interview.department || 'Engineering',
      interviewId: interview._id,
      applicationId: interview.applicationId
    };

    // Navigate to HR login page with candidate data as state
    navigate("/hr-login", {
      state: {
        candidateData: candidateData,
        fromInterview: true,
        interviewId: interview._id
      }
    });
  };

  // Auth success callback - DON'T navigate to onboarding
  // Auth success callback - Navigate to dashboard
  // Auth success callback - Navigate to dashboard
  // In your parent component that uses AuthModal
  const handleAuthSuccess = (userData) => {
    console.log('Auth successful, user data:', userData);

    // Close the auth modal
    setAuthModalOpen(false);

    // Navigate to dashboard
    navigate("/dashboard", {
      replace: true,
      state: {
        user: userData,
        fromAuth: true,
        timestamp: Date.now()
      }
    });
  };


  // Get current user from sessionStorage
  const getCurrentUser = () => {
    try {
      // Get user data from sessionStorage (set during login/signup)
      const userData = sessionStorage.getItem('userData');
      const userEmail = sessionStorage.getItem('userEmail');
      const userName = sessionStorage.getItem('userName');
      const userCompany = sessionStorage.getItem('userCompany');

      if (userData) {
        const parsedUser = JSON.parse(userData);
        return {
          email: parsedUser.email || userEmail,
          name: parsedUser.name || userName,
          company: parsedUser.company || userCompany,
          userId: parsedUser.userId || parsedUser._id
        };
      }

      // Fallback to individual sessionStorage items
      if (userEmail) {
        return {
          email: userEmail,
          name: userName || 'HR Manager',
          company: userCompany || 'Your Company',
          userId: sessionStorage.getItem('userId')
        };
      }

      // Final fallback - try to get from token
      const token = sessionStorage.getItem('authToken') || sessionStorage.getItem('token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          return {
            email: payload.email,
            name: payload.name || 'HR Manager',
            company: payload.company || 'Your Company',
            userId: payload.userId || payload._id
          };
        } catch (tokenError) {
          console.error('Error decoding token:', tokenError);
        }
      }

      console.warn('No user data found in sessionStorage');
      return null;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  };

  useEffect(() => {
    loadInterviews();
  }, [view, selectedDate]);

  // Add this state to store all loaded interviews before filtering

  // Update loadInterviews function to set allInterviews
  const loadInterviews = async () => {
    try {
      setLoading(true);
      setError('');
      const token = sessionStorage.getItem('authToken') || sessionStorage.getItem('token');
      const currentUser = getCurrentUser();

      if (!token) {
        setError('Authentication token not found. Please login again.');
        setLoading(false);
        return;
      }

      if (!currentUser) {
        setError('Unable to identify current user. Please login again.');
        setLoading(false);
        return;
      }

      console.log('🔄 Loading interviews for user:', currentUser.email);

      try {
        // Try the dedicated interviews endpoint first
        const response = await axios.get(
          `${API_BASE_URL}/api/applications/interviews/upcoming`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            },
            params: {
              date: selectedDate || (view === 'today' ? new Date().toISOString().split('T')[0] : undefined),
              userEmail: currentUser.email
            }
          }
        );

        console.log('📡 API Response:', response.data);

        if (response.data.success) {
          let loadedInterviews = response.data.data || [];

          // Double filter by current user
          loadedInterviews = loadedInterviews.filter(interview =>
            interview.userEmail === currentUser.email ||
            interview.createdBy === currentUser.email ||
            interview.assignedTo === currentUser.email ||
            !interview.userEmail
          );

          console.log('✅ Interviews loaded from API for current user:', loadedInterviews.length);

          // Store all interviews
          setAllInterviews(loadedInterviews);

          // Apply view filtering (but not search filtering here)
          const viewFiltered = filterInterviewsByView(loadedInterviews, view);
          setInterviews(viewFiltered);
        } else {
          setError('Failed to load interviews: ' + (response.data.message || 'Unknown error'));
        }
      } catch (apiError) {
        console.error('❌ API endpoint failed, trying fallback...', apiError);
        await loadInterviewsFromApplications(token, currentUser);
      }

    } catch (error) {
      console.error('💥 Error loading interviews:', error);
      setError('Failed to load interviews. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Update loadInterviewsFromApplications to also set allInterviews
  const loadInterviewsFromApplications = async (token, currentUser) => {
    try {
      console.log('🔄 Using fallback method to load interviews for user:', currentUser.email);

      // Load requirements for current user
      const response = await axios.get(`${API_BASE_URL}/api/requirements`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        const requirements = response.data.data || [];

        // Filter requirements by current user
        const userRequirements = requirements.filter(req =>
          req.userEmail === currentUser.email ||
          req.createdBy === currentUser.email ||
          !req.userEmail
        );

        const requirementIds = userRequirements.map(req => req._id);
        const allApplications = [];

        console.log('📋 User requirements found:', userRequirements.length);

        // Load applications for each requirement
        for (const requirementId of requirementIds) {
          try {
            const appsResponse = await axios.get(
              `${API_BASE_URL}/api/applications/requirement/${requirementId}`,
              {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              }
            );

            if (appsResponse.data.success) {
              // Filter applications by current user
              const userApplications = appsResponse.data.data.filter(app =>
                app.userEmail === currentUser.email ||
                app.createdBy === currentUser.email ||
                !app.userEmail
              );
              allApplications.push(...userApplications);
            }
          } catch (error) {
            console.error(`Error loading applications for requirement ${requirementId}:`, error);
          }
        }

        console.log('📋 Total applications loaded for current user:', allApplications.length);

        // Extract interviews from applications
        const loadedInterviews = [];
        allApplications.forEach(application => {
          // Main interview
          if (application.interview && application.interview.scheduledDate) {
            loadedInterviews.push({
              _id: application._id + '_interview',
              applicationId: application._id,
              candidateName: application.name,
              candidateEmail: application.email,
              candidatePhone: application.phone,
              position: application.appliedRole,
              companyName: application.companyName || 'Your Company',
              applicationStatus: application.status,
              scheduledDate: application.interview.scheduledDate,
              interviewType: application.interview.interviewType || 'Technical',
              interviewMode: application.interview.interviewMode || 'Video Call',
              interviewers: application.interview.interviewers || [],
              meetingLink: application.interview.meetingLink || '',
              location: application.interview.location || '',
              notes: application.interview.notes || '',
              duration: application.interview.duration || 60,
              status: application.interview.status || 'Scheduled',
              interviewNumber: 1,
              totalInterviews: 1,
              feedback: application.interview.feedback,
              assessmentId: application.interview.assessmentId,
              userEmail: application.userEmail || currentUser.email,
              createdBy: application.createdBy || currentUser.email,
              department: application.department
            });
          }

          // Additional interviews
          if (application.interviews && application.interviews.length > 0) {
            application.interviews.forEach((interview, index) => {
              if (interview.scheduledDate) {
                loadedInterviews.push({
                  _id: interview._id || `${application._id}_interview_${index}`,
                  applicationId: application._id,
                  candidateName: application.name,
                  candidateEmail: application.email,
                  candidatePhone: application.phone,
                  position: application.appliedRole,
                  companyName: application.companyName || 'Your Company',
                  applicationStatus: application.status,
                  scheduledDate: interview.scheduledDate,
                  interviewType: interview.interviewType || 'Technical',
                  interviewMode: interview.interviewMode || 'Video Call',
                  interviewers: interview.interviewers || [],
                  meetingLink: interview.meetingLink || '',
                  location: interview.location || '',
                  notes: interview.notes || '',
                  duration: interview.duration || 60,
                  status: interview.status || 'Scheduled',
                  interviewNumber: index + 1,
                  totalInterviews: application.interviews.length,
                  feedback: interview.feedback,
                  assessmentId: interview.assessmentId,
                  userEmail: application.userEmail || currentUser.email,
                  createdBy: application.createdBy || currentUser.email,
                  department: application.department
                });
              }
            });
          }
        });

        console.log('🎯 Total interviews found for current user:', loadedInterviews.length);

        // Store all interviews
        setAllInterviews(loadedInterviews);

        // Apply view filtering (but not search filtering here)
        const viewFiltered = filterInterviewsByView(loadedInterviews, view);
        setInterviews(viewFiltered);
      }
    } catch (fallbackError) {
      console.error('💥 Fallback method also failed:', fallbackError);
      setError('Failed to load interviews. Please check your connection and try again.');
    }
  };

  // Update filterInterviewsByView to remove searchTerm filtering
  const filterInterviewsByView = (interviewsToFilter, currentView) => {
    const now = new Date();
    let filteredInterviews = interviewsToFilter;

    switch (currentView) {
      case 'upcoming':
        filteredInterviews = interviewsToFilter.filter(interview =>
          new Date(interview.scheduledDate) > now && interview.status === 'Scheduled'
        );
        break;
      case 'today':
        const today = new Date();
        const todayStart = new Date(today.setHours(0, 0, 0, 0));
        const todayEnd = new Date(today.setHours(23, 59, 59, 999));
        filteredInterviews = interviewsToFilter.filter(interview => {
          const interviewDate = new Date(interview.scheduledDate);
          return interviewDate >= todayStart && interviewDate <= todayEnd && interview.status === 'Scheduled';
        });
        break;
      case 'completed':
        filteredInterviews = interviewsToFilter.filter(interview =>
          interview.status === 'Completed'
        );
        break;
      case 'next-round':
        filteredInterviews = interviewsToFilter.filter(interview =>
          interview.feedback?.decision === 'Move to Next Round'
        );
        break;
      case 'hire':
        filteredInterviews = interviewsToFilter.filter(interview =>
          interview.feedback?.decision === 'Hire' ||
          interview.status === 'Hired'
        );
        break;
      case 'assessments':
        filteredInterviews = interviewsToFilter.filter(interview =>
          interview.interviewType === 'Assessment' ||
          interview.assessmentId ||
          (interview.interviewType && interview.interviewType.toLowerCase().includes('assessment'))
        );
        break;
      case 'all':
        filteredInterviews = interviewsToFilter;
        break;
      default:
        filteredInterviews = interviewsToFilter;
    }

    if (selectedDate) {
      const selectedDateObj = new Date(selectedDate);
      const selectedDateStart = new Date(selectedDateObj.setHours(0, 0, 0, 0));
      const selectedDateEnd = new Date(selectedDateObj.setHours(23, 59, 59, 999));

      filteredInterviews = filteredInterviews.filter(interview => {
        const interviewDate = new Date(interview.scheduledDate);
        return interviewDate >= selectedDateStart && interviewDate <= selectedDateEnd;
      });
    }

    return filteredInterviews.sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));
  };

  // Add this useEffect to handle search filtering
  useEffect(() => {
    if (!searchTerm.trim()) {
      // If search term is empty, just apply view filtering
      const viewFiltered = filterInterviewsByView(allInterviews, view);
      setInterviews(viewFiltered);
    } else {
      // First apply view filtering, then search filtering
      const viewFiltered = filterInterviewsByView(allInterviews, view);

      const searchFiltered = viewFiltered.filter(interview =>
        interview.candidateName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        interview.position?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        interview.interviewType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        interview.candidateEmail?.toLowerCase().includes(searchTerm.toLowerCase())
      );

      setInterviews(searchFiltered);
    }
  }, [searchTerm, view, selectedDate, allInterviews]);

  // Update the main useEffect to remove view/selectedDate filtering
  useEffect(() => {
    loadInterviews();
    // Remove view and selectedDate from dependencies here since we handle them separately
  }, []); // Empty dependency array - load once on mount

  // Update refreshInterviews to also trigger search re-filtering
  // const refreshInterviews = () => {
  //   loadInterviews();
  // };

  // Open Offer Letter Popup
  const handleOpenOfferLetter = (interview) => {
    const currentUser = getCurrentUser();
    setSelectedCandidate({
      _id: interview.applicationId,
      name: interview.candidateName,
      email: interview.candidateEmail,
      jobTitle: interview.position,
      department: interview.department || 'Engineering',
      address: interview.location || '',
      userEmail: currentUser?.email,
      createdBy: currentUser?.email
    });
    setShowOfferLetter(true);
  };

  // Close Offer Letter Popup
  const handleCloseOfferLetter = () => {
    setShowOfferLetter(false);
    setSelectedCandidate(null);
  };

  // Handle Offer Letter Save
  const handleOfferLetterSave = () => {
    loadInterviews();
    alert('Offer letter sent successfully!');
  };

  const loadCandidateAnswers = async (assessmentId) => {
    try {
      setAnswersLoading(true);
      setCandidateAnswers([]);
      setSelectedAssessment(assessmentId);

      const token = sessionStorage.getItem('authToken');
      const currentUser = getCurrentUser();

      console.log('📝 Loading candidate answers for assessment:', assessmentId);

      const response = await axios.get(
        `${API_BASE_URL}/api/assessments/${assessmentId}/submissions`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      console.log('📝 Submissions API response:', response.data);

      if (response.data.success) {
        const submissions = response.data.data || [];
        console.log('📝 Loaded submissions:', submissions.length);
        setCandidateAnswers(submissions);
        setShowAnswersModal(true);
      } else {
        alert('No submissions found for this assessment.');
      }
    } catch (error) {
      console.error('Error loading candidate answers:', error);
      if (error.response?.status === 404) {
        alert('No submissions found for this assessment yet.');
      } else {
        alert('Failed to load candidate answers. Please try again.');
      }
    } finally {
      setAnswersLoading(false);
    }
  };

  const viewSubmissionDetails = async (submissionId) => {
    try {
      setAnswersLoading(true);
      const token = sessionStorage.getItem('authToken');
      const response = await axios.get(
        `${API_BASE_URL}/api/assessments/submissions/${submissionId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setDetailedSubmission(response.data.data);
        setShowDetailedView(true);
      } else {
        alert('Failed to load submission details.');
      }
    } catch (error) {
      console.error('Error loading submission details:', error);
      alert('Failed to load submission details.');
    } finally {
      setAnswersLoading(false);
    }
  };

  const closeDetailedView = () => {
    setShowDetailedView(false);
    setDetailedSubmission(null);
  };

  const hasAssessment = (interview) => {
    return interview.assessmentId ||
      interview.interviewType === 'Assessment' ||
      (interview.interviewType && interview.interviewType.toLowerCase().includes('assessment'));
  };

  const openScheduleNextRound = (interview) => {
    setSelectedInterview(interview);

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const defaultDate = tomorrow.toISOString().split('T')[0];
    const defaultTime = '10:00';

    setScheduleData({
      scheduledDate: defaultDate,
      scheduledTime: defaultTime,
      interviewType: getNextRoundType(interview),
      interviewMode: interview.interviewMode || 'Video Call',
      interviewers: [''],
      meetingLink: '',
      location: '',
      notes: `Next round interview for ${interview.candidateName} - ${interview.position}`,
      duration: 60
    });

    setShowScheduleModal(true);
  };

  const getNextRoundType = (interview) => {
    const currentType = interview.interviewType || 'Technical';
    const roundNumber = interview.interviewNumber || 1;

    switch (currentType) {
      case 'Technical':
        return roundNumber === 1 ? 'Technical Round 2' : 'Managerial';
      case 'Technical Round 2':
        return 'Managerial';
      case 'Managerial':
        return 'HR';
      case 'HR':
        return 'Final';
      default:
        return `Round ${roundNumber + 1}`;
    }
  };

  const scheduleNextRound = async () => {
    if (!selectedInterview) return;

    try {
      const token = sessionStorage.getItem('authToken');
      const currentUser = getCurrentUser();

      if (!currentUser) {
        alert('User not found. Please log in again.');
        return;
      }

      const scheduledDateTime = new Date(`${scheduleData.scheduledDate}T${scheduleData.scheduledTime}`);

      let meetingLink = scheduleData.meetingLink;
      let assessmentId = null;

      if (scheduleData.interviewType === 'Assessment') {
        try {
          console.log('🔄 Creating assessment for:', selectedInterview.candidateName);

          const assessmentResponse = await axios.post(
            `${API_BASE_URL}/api/assessments`,
            {
              title: `Assessment for ${selectedInterview.candidateName} - ${selectedInterview.position}`,
              description: `Technical assessment for ${selectedInterview.candidateName}`,
              duration: parseInt(scheduleData.duration),
              candidateName: selectedInterview.candidateName,
              candidateEmail: selectedInterview.candidateEmail,
              applicationId: selectedInterview.applicationId,
              position: selectedInterview.position,
              userEmail: currentUser.email,
              questions: []
            },
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );

          if (assessmentResponse.data.success) {
            assessmentId = assessmentResponse.data.data._id;
            meetingLink = `${window.location.origin}/assessment/${assessmentId}?candidateEmail=${encodeURIComponent(selectedInterview.candidateEmail)}&applicationId=${selectedInterview.applicationId}`;
            console.log('✅ Assessment created successfully:', assessmentId);
          } else {
            throw new Error(assessmentResponse.data.message || 'Failed to create assessment');
          }
        } catch (assessmentError) {
          console.error('❌ Error creating assessment:', assessmentError);

          let errorMessage = 'Failed to create assessment. ';
          if (assessmentError.response?.data?.message) {
            errorMessage += assessmentError.response.data.message;
          } else if (assessmentError.response?.data?.errors) {
            errorMessage += assessmentError.response.data.errors.join(', ');
          } else {
            errorMessage += 'Please try again.';
          }

          alert(errorMessage);
          return;
        }
      }

      const interviewData = {
        scheduledDate: scheduledDateTime.toISOString(),
        interviewType: scheduleData.interviewType,
        interviewMode: scheduleData.interviewMode,
        interviewers: scheduleData.interviewers.filter(interviewer => interviewer.trim() !== ''),
        meetingLink: meetingLink,
        location: scheduleData.location,
        notes: scheduleData.notes,
        duration: parseInt(scheduleData.duration),
        assessmentId: assessmentId,
        userEmail: currentUser.email,
        createdBy: currentUser.email
      };

      console.log('📅 Scheduling next round:', interviewData);

      const response = await axios.post(
        `${API_BASE_URL}/api/applications/${selectedInterview.applicationId}/interview`,
        interviewData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setShowScheduleModal(false);
        setSelectedInterview(null);

        if (scheduleData.interviewType === 'Assessment') {
          alert('Assessment scheduled successfully! Candidate will receive the assessment link.');
        } else {
          alert('Next round interview scheduled successfully!');
        }

        loadInterviews();
      }
    } catch (error) {
      console.error('❌ Error scheduling next round:', error);
      alert('Failed to schedule next round: ' + (error.response?.data?.message || error.message));
    }
  };

  const addInterviewer = () => {
    setScheduleData(prev => ({
      ...prev,
      interviewers: [...prev.interviewers, '']
    }));
  };

  const removeInterviewer = (index) => {
    setScheduleData(prev => ({
      ...prev,
      interviewers: prev.interviewers.filter((_, i) => i !== index)
    }));
  };

  const updateInterviewer = (index, value) => {
    setScheduleData(prev => ({
      ...prev,
      interviewers: prev.interviewers.map((item, i) => i === index ? value : item)
    }));
  };

  const handleJoinMeeting = (meetingLink) => {
    if (meetingLink) {
      window.open(meetingLink, '_blank', 'noopener,noreferrer');
    } else {
      alert('No meeting link provided for this interview');
    }
  };

  const updateInterviewStatus = async (interviewId, newStatus, decision = null) => {
    try {
      const token = sessionStorage.getItem('authToken');
      const currentUser = getCurrentUser();

      console.log('🔄 Updating interview status:', { interviewId, newStatus, decision });

      const response = await axios.patch(
        `${API_BASE_URL}/api/applications/interviews/${interviewId}/status`,
        {
          status: newStatus,
          decision: decision,
          userEmail: currentUser?.email
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setInterviews(prev => prev.map(interview =>
          interview._id === interviewId ? { ...interview, ...response.data.data } : interview
        ));

        let message = `Interview marked as ${newStatus}`;
        if (newStatus === 'Completed' || newStatus === 'Cancelled' || newStatus === 'Rejected') {
          message += ' and email sent to candidate';
        }

        alert(message);
        loadInterviews();
      }
    } catch (error) {
      console.error('Error updating interview status:', error);
      alert('Failed to update interview status: ' + (error.response?.data?.message || error.message));
    }
  };

  const openFeedbackModal = (interview) => {
    setSelectedInterview(interview);
    setFeedback({
      rating: 5,
      comments: '',
      strengths: [''],
      areasForImprovement: [''],
      decision: 'Move to Next Round'
    });
    setShowFeedbackModal(true);
  };

  const submitFeedback = async () => {
    if (!selectedInterview) return;

    try {
      const token = sessionStorage.getItem('authToken');
      const currentUser = getCurrentUser();

      console.log('📝 Submitting feedback for interview:', selectedInterview._id);

      if (!feedback.decision) {
        alert('Please select a decision');
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/applications/interviews/${selectedInterview._id}/feedback`,
        {
          feedback,
          userEmail: currentUser?.email
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setInterviews(prev => prev.map(interview =>
          interview._id === selectedInterview._id ? { ...interview, ...response.data.data } : interview
        ));

        setShowFeedbackModal(false);
        setSelectedInterview(null);

        let message = 'Feedback submitted successfully';
        if (response.data.message && response.data.message.includes('email sent')) {
          message += ' and email sent to candidate';
        }

        if (feedback.decision === 'Move to Next Round') {
          message += `. ${selectedInterview.candidateName} moved to next round!`;
        } else if (feedback.decision === 'Hire') {
          message += `. 🎉 Congratulations! ${selectedInterview.candidateName} has been hired!`;
        } else if (feedback.decision === 'Reject') {
          message += `. Candidate has been rejected.`;
        }

        alert(message);
        loadInterviews();
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback: ' + (error.response?.data?.message || error.message));
    }
  };

  const addStrength = () => {
    setFeedback(prev => ({
      ...prev,
      strengths: [...prev.strengths, '']
    }));
  };

  const removeStrength = (index) => {
    setFeedback(prev => ({
      ...prev,
      strengths: prev.strengths.filter((_, i) => i !== index)
    }));
  };

  const updateStrength = (index, value) => {
    setFeedback(prev => ({
      ...prev,
      strengths: prev.strengths.map((item, i) => i === index ? value : item)
    }));
  };

  const addAreaForImprovement = () => {
    setFeedback(prev => ({
      ...prev,
      areasForImprovement: [...prev.areasForImprovement, '']
    }));
  };

  const removeAreaForImprovement = (index) => {
    setFeedback(prev => ({
      ...prev,
      areasForImprovement: prev.areasForImprovement.filter((_, i) => i !== index)
    }));
  };

  const updateAreaForImprovement = (index, value) => {
    setFeedback(prev => ({
      ...prev,
      areasForImprovement: prev.areasForImprovement.map((item, i) => i === index ? value : item)
    }));
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-IN', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }),
      time: date.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
    };
  };

  const isMeetingStartingSoon = (scheduledDate) => {
    const now = new Date();
    const interviewTime = new Date(scheduledDate);
    const timeDiff = interviewTime - now;
    return timeDiff > 0 && timeDiff <= 30 * 60 * 1000;
  };

  const refreshInterviews = () => {
    loadInterviews();
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Scheduled': return 'iv-status-badge iv-status-scheduled';
      case 'Completed': return 'iv-status-badge iv-status-completed';
      case 'Cancelled': return 'iv-status-badge iv-status-cancelled';
      case 'No Show': return 'iv-status-badge iv-status-no-show';
      case 'Rejected': return 'iv-status-badge iv-status-rejected';
      case 'Hired': return 'iv-status-badge iv-status-hired';
      case 'Move to Next Round': return 'iv-status-badge iv-status-next-round';
      default: return 'iv-status-badge iv-status-scheduled';
    }
  };

  const getDecisionBadgeClass = (decision) => {
    switch (decision) {
      case 'Move to Next Round': return 'iv-decision-badge iv-decision-next-round';
      case 'Hire': return 'iv-decision-badge iv-decision-hire';
      case 'Reject': return 'iv-decision-badge iv-decision-reject';
      case 'On Hold': return 'iv-decision-badge iv-decision-on-hold';
      default: return 'iv-decision-badge iv-decision-default';
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const renderAnswerContent = (question, answer) => {
    if (!answer) return <span className="iv-no-answer">No answer provided</span>;

    switch (question.type) {
      case 'multiple_choice':
        return (
          <div className="iv-multiple-choice-answer">
            <strong>Selected:</strong> {answer}
            {question.correctAnswer && (
              <span className={`iv-correctness ${answer === question.correctAnswer ? 'iv-correct' : 'iv-incorrect'}`}>
                {answer === question.correctAnswer ? ' ✓ Correct' : ' ✗ Incorrect'}
              </span>
            )}
          </div>
        );

      case 'coding':
        return (
          <div className="iv-coding-answer">
            <strong>Code Solution:</strong>
            <pre className="iv-code-block">{answer}</pre>
          </div>
        );

      case 'text':
        return (
          <div className="iv-text-answer">
            <strong>Written Answer:</strong>
            <p className="iv-answer-text">{answer}</p>
          </div>
        );

      default:
        return <span>{answer}</span>;
    }
  };

  const getInterviewStats = () => {
    const stats = {
      total: interviews.length,
      upcoming: interviews.filter(i => i.status === 'Scheduled' && new Date(i.scheduledDate) > new Date()).length,
      completed: interviews.filter(i => i.status === 'Completed').length,
      assessments: interviews.filter(hasAssessment).length,
      hired: interviews.filter(i => i.feedback?.decision === 'Hire' || i.status === 'Hired').length
    };
    return stats;
  };

  // Display current user info
  const renderCurrentUserInfo = () => {
    const currentUser = getCurrentUser();
    if (!currentUser) return null;

    return (
      <div className="iv-user-info">
        <div className="iv-user-avatar">
          {currentUser.name?.charAt(0)?.toUpperCase() || 'U'}
        </div>
        <div className="iv-user-details">
          <span className="iv-user-name">{currentUser.name}</span>
          <span className="iv-user-email">{currentUser.email}</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="iv-loading-container">
        <div className="iv-loading-animation">
          <div className="iv-loading-spinner"></div>
          <div className="iv-loading-pulse"></div>
        </div>
        <p className="iv-loading-text">Loading your interviews...</p>
      </div>
    );
  }

  const stats = getInterviewStats();
  const currentUser = getCurrentUser();

  return (
    <div className="int-dashboard-container">
      {/* Animated Background Elements */}
      <div className="int-background-animation">
        <div className="int-floating-circle int-circle-1"></div>
        <div className="int-floating-circle int-circle-2"></div>
        <div className="int-floating-circle int-circle-3"></div>
        <div className="int-floating-circle int-circle-4"></div>
        <div className="int-pulse-dot int-pulse-1"></div>
        <div className="int-pulse-dot int-pulse-2"></div>
      </div>

      {/* Header Section */}
      <div className="int-header-section">
        <div className="int-header-content">
          <div className="int-header-title">
            <h1 className="int-main-title">
              <span className="int-title-icon">
                <FaBullseye />
              </span>
              My Interview Dashboard
            </h1>
            <p className="int-subtitle">
              Manage and track your interview sessions
            </p>
            {currentUser && (
              <div className="int-welcome-message">
                Welcome back, <strong>{currentUser.name}</strong>
                <FaSmile
                  style={{
                    marginLeft: "6px",
                    verticalAlign: "middle",
                    color: "#e54646"
                  }}
                />
                {interviews.length > 0
                  ? ` You have ${interviews.length} interviews.`
                  : " No interviews scheduled yet."}
              </div>
            )}
          </div>
          <div className="int-header-actions">
            <button onClick={refreshInterviews} className="int-refresh-btn">
              <span className="int-refresh-icon">
                <FaSyncAlt />
              </span>
              Refresh
            </button>
          </div>
        </div>
      </div>


      {/* Statistics Cards */}
      <div className="int-stats-grid">
        <div className="int-stat-card int-stat-primary">
          <div className="int-stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
          </div>
          <div className="int-stat-content">
            <h3 className="int-stat-number">{stats.total}</h3>
            <p className="int-stat-label">My Interviews</p>
          </div>
        </div>
        <div className="int-stat-card int-stat-success">
          <div className="int-stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
          </div>
          <div className="int-stat-content">
            <h3 className="int-stat-number">{stats.upcoming}</h3>
            <p className="int-stat-label">Upcoming</p>
          </div>
          {/* <div className="int-stat-glow"></div> */}
        </div>

        <div className="int-stat-card int-stat-warning">
          <div className="int-stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <div className="int-stat-content">
            <h3 className="int-stat-number">{stats.completed}</h3>
            <p className="int-stat-label">Completed</p>
          </div>
          {/* <div className="int-stat-glow"></div> */}
        </div>

        <div className="int-stat-card int-stat-info">
          <div className="int-stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          </div>
          <div className="int-stat-content">
            <h3 className="int-stat-number">{stats.hired}</h3>
            <p className="int-stat-label">Hired</p>
          </div>
          {/* <div className="int-stat-glow"></div> */}
        </div>
      </div>

      {/* Controls Section */}
      <div className="int-controls-section">
        <div className="int-search-filter">
          <div className="int-search-box">
            <span className="int-search-icon"></span>
            <input
              type="text"
              placeholder="Search my candidates, positions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="int-search-input"
            />
          </div>

          <div className="int-date-filter">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="int-date-input"
            />
            {selectedDate && (
              <button
                onClick={() => setSelectedDate('')}
                className="int-clear-date-btn"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* View Tabs */}
        <div className="int-view-tabs">
          <button
            className={`int-tab-btn ${view === 'upcoming' ? 'int-tab-active' : ''}`}
            onClick={() => setView('upcoming')}
          >
            <span className="int-tab-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </span>
            My Upcoming
          </button>

          <button
            className={`int-tab-btn ${view === 'today' ? 'int-tab-active' : ''}`}
            onClick={() => setView('today')}
          >
            <span className="int-tab-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </span>
            Today
          </button>

          <button
            className={`int-tab-btn ${view === 'completed' ? 'int-tab-active' : ''}`}
            onClick={() => setView('completed')}
          >
            <span className="int-tab-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </span>
            Completed
          </button>

          <button
            className={`int-tab-btn ${view === 'next-round' ? 'int-tab-active' : ''}`}
            onClick={() => setView('next-round')}
          >
            <span className="int-tab-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12a9 9 0 11-6.219-8.56"></path>
                <path d="M12 2v10l3 3"></path>
              </svg>
            </span>
            Next Round
          </button>

          <button
            className={`int-tab-btn ${view === 'hire' ? 'int-tab-active' : ''}`}
            onClick={() => setView('hire')}
          >
            <span className="int-tab-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </span>
            My Hires
          </button>

          <button
            className={`int-tab-btn ${view === 'assessments' ? 'int-tab-active' : ''}`}
            onClick={() => setView('assessments')}
          >
            <span className="int-tab-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 20V10"></path>
                <path d="M12 20V4"></path>
                <path d="M6 20v-6"></path>
              </svg>
            </span>
            My Assessments
          </button>

          <button
            className={`int-tab-btn ${view === 'all' ? 'int-tab-active' : ''}`}
            onClick={() => setView('all')}
          >
            <span className="int-tab-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
                <line x1="8" y1="14" x2="8" y2="18"></line>
                <line x1="12" y1="14" x2="12" y2="18"></line>
                <line x1="16" y1="14" x2="16" y2="18"></line>
              </svg>
            </span>
            All My Interviews
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="int-error-message">
          <div className="int-error-icon">⚠️</div>
          <p>{error}</p>
          <button onClick={refreshInterviews} className="int-retry-btn">
            Try Again
          </button>
        </div>
      )}

      {/* Interviews Grid */}
      {interviews.length > 0 ? (
        <div className="int-interviews-section">
          <div className="int-section-header">
            <h3 className="int-section-title">
              {view === 'upcoming' && `My Upcoming Interviews`}
              {view === 'today' && `Today's Interviews`}
              {view === 'completed' && `My Completed Interviews`}
              {view === 'next-round' && `My Next Round Candidates`}
              {view === 'hire' && `My Hired Candidates`}
              {view === 'assessments' && `My Assessment Interviews`}
              {view === 'all' && `All My Interviews`}
              <span className="int-count-badge">{interviews.length}</span>
            </h3>
          </div>

          <div className="int-interviews-grid">
            {interviews.map(interview => {
              const { date, time } = formatDateTime(interview.scheduledDate);
              const startingSoon = isMeetingStartingSoon(interview.scheduledDate);
              const isAssessment = hasAssessment(interview);
              const isHired = interview.feedback?.decision === 'Hire' || interview.status === 'Hired';

              return (
                <div key={interview._id} className={`int-interview-card ${interview.status.toLowerCase()} ${isAssessment ? 'int-assessment-interview' : ''} ${isHired ? 'int-hired-interview' : ''}`}>
                  <div className="int-card-glow"></div>

                  <div className="int-interview-header">
                    <div className="int-candidate-info">
                      <div className="int-candidate-avatar">
                        {interview.candidateName?.charAt(0)?.toUpperCase() || 'C'}
                      </div>
                      <div className="int-candidate-details">
                        <h4 className="int-candidate-name">{interview.candidateName}</h4>
                        <p className="int-candidate-role">{interview.position}</p>
                        <div className="int-contact-info">
                          <span className="int-contact-item">{interview.candidateEmail}</span>
                          <span className="int-contact-divider">•</span>
                          <span className="int-contact-item">{interview.candidatePhone}</span>
                        </div>
                      </div>
                    </div>

                    <div className="int-interview-meta">
                      {startingSoon && interview.status === 'Scheduled' && (
                        <span className="int-starting-soon-badge">
                          <span className="int-pulse-dot"></span>
                          Starting Soon
                        </span>
                      )}
                      <span className={getStatusBadgeClass(interview.status)}>
                        {interview.status}
                      </span>
                      {interview.feedback?.decision && (
                        <span className={getDecisionBadgeClass(interview.feedback.decision)}>
                          {interview.feedback.decision}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="int-interview-details">
                    <div className="int-detail-row">
                      <div className="int-detail-item">
                        <span className="int-detail-icon">
                          <FaCalendarAlt />
                        </span>
                        <div className="int-detail-content">
                          <label>Date & Time</label>
                          <span>{date} at {time}</span>
                        </div>
                      </div>

                      <div className="int-detail-item">
                        <span className="int-detail-icon">
                          <FaBullseye />
                        </span>
                        <div className="int-detail-content">
                          <label>Type</label>
                          <span>{interview.interviewType || 'Technical'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="int-detail-row">
                      <div className="int-detail-item">
                         <span className="int-detail-icon">
                            <FaUsers />
                         </span>
                        <div className="int-detail-content">
                          <label>Interviewers</label>
                          <span>{(interview.interviewers && interview.interviewers.length > 0) ? interview.interviewers.join(', ') : 'Not specified'}</span>
                        </div>
                      </div>

                      <div className="int-detail-item">
                        <span className="int-detail-icon">
                          <FaClock />
                        </span>
                        <div className="int-detail-content">
                          <label>Duration</label>
                          <span>{interview.duration || 60} minutes</span>
                        </div>
                      </div>
                    </div>

                    {interview.totalInterviews > 1 && (
                      <div className="int-interview-progress">
                        <div className="int-progress-bar">
                          <div
                            className="int-progress-fill"
                            style={{ width: `${(interview.interviewNumber / interview.totalInterviews) * 100}%` }}
                          ></div>
                        </div>
                        <span className="int-progress-text">
                          Round {interview.interviewNumber} of {interview.totalInterviews}
                        </span>
                      </div>
                    )}

                    {isAssessment && (
                      <div className="int-assessment-indicator">
                        <span className="int-assessment-icon">
                          <FaChartBar />
                        </span>

                        Assessment Required
                      </div>
                    )}

                    {/* Hired Candidate Special Info */}
                    {isHired && (
                      <div className="int-hired-indicator">
                       <span className="int-hired-icon"><FaStar size={20} color="#fb2424" /></span>

                        Candidate Selected for Hire!
                      </div>
                    )}
                  </div>

                  <div className="int-interview-actions">
                    {interview.meetingLink && interview.status === 'Scheduled' ? (
                      <button
                        className="int-join-meeting-btn"
                        onClick={() => handleJoinMeeting(interview.meetingLink)}
                      >
                        <span className="int-btn-icon"><FaVideo /></span>
                        Join Meeting
                      </button>
                    ) : interview.interviewMode === 'In-Person' && interview.status === 'Scheduled' ? (
                      <div className="int-location-info">
                        <span className="int-location-icon"> <FaMapMarkerAlt /></span>

                        {interview.location || 'Location not specified'}
                      </div>
                    ) : null}

                    {interview.status === 'Scheduled' && (
                      <div className="int-action-buttons">
                        <button
                          className="int-action-btn int-completed-btn"
                          onClick={() => openFeedbackModal(interview)}
                        >
                          <span className="int-btn-icon"> <FaCheck /> </span>

                          Complete
                        </button>

                        <button
                          className="int-action-btn int-no-show-btn"
                          onClick={() => updateInterviewStatus(interview._id, 'No Show')}
                        >
                          <span className="int-btn-icon"><FaTimes /></span>
                          No Show
                        </button>
                      </div>
                    )}

                    {interview.status === 'Completed' && (
                      <div className="int-action-buttons">
                        <button
                          className="int-action-btn int-feedback-btn"
                          onClick={() => openFeedbackModal(interview)}
                        >
                          <span className="int-btn-icon">  <FaPen /> </span>

                          View Feedback
                        </button>

                        {interview.feedback?.decision === 'Move to Next Round' && (
                          <button
                            className="int-action-btn int-schedule-next-btn"
                            onClick={() => openScheduleNextRound(interview)}
                          >
                            <span className="int-btn-icon">  <FaCalendarAlt /> </span>

                            Schedule Next
                          </button>
                        )}

                        {hasAssessment(interview) && (
                          <button
                            className="int-action-btn int-answers-btn"
                            onClick={() => {
                              setSelectedAssessment(interview.assessmentId);
                              loadCandidateAnswers(interview.assessmentId);
                            }}
                            disabled={answersLoading}
                          >
                           <span className="int-btn-icon">  <FaChartBar /> </span>


                            {answersLoading ? 'Loading...' : 'View Answers'}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Actions for Hired Candidates */}
                    {(interview.feedback?.decision === 'Hire' || interview.status === 'Hired') && (
                      <div className="int-action-buttons">
                        <button
                          className="int-action-btn int-offer-btn"
                          onClick={() => handleOpenOfferLetter(interview)}
                        >
                         <span className="int-btn-icon">  <FaFileAlt /></span>

                          Generate Offer Letter
                        </button>

                        <button
                          className="int-action-btn int-onboard-btn"
                          onClick={() => handleRecruit(interview)} // Pass interview data
                        >
                          <span className="int-btn-icon">  <FaRocket /> </span>

                          Start Onboarding
                        </button>
                      </div>
                    )}

                    {/* Auth Modal */}
                    {authModalOpen && (
                      <AuthModal
                        isOpen={authModalOpen}
                        onClose={() => {
                          setAuthModalOpen(false);
                          setOnboardingCandidate(null);
                        }}
                        initialIsLoginForm={authModalType === 'login'}
                        onSuccess={handleAuthSuccess}
                        candidateData={onboardingCandidate}
                      />
                    )}

                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="int-empty-state">
          <div className="int-empty-animation">
          <div className="int-empty-icon">
            {view === "hire" ? <FaStar /> : <FaCalendarAlt />}
          </div>

            <div className="int-empty-pulse"></div>
          </div>
          <h3 className="int-empty-title">
            {view === 'hire' ? 'No Hired Candidates Yet' : 'No Interviews Found'}
          </h3>
          <p className="int-empty-description">
            {view === 'upcoming' && "You don't have any upcoming interviews scheduled."}
            {view === 'today' && "No interviews scheduled for today."}
            {view === 'completed' && "No completed interviews yet."}
            {view === 'next-round' && "No candidates moved to next round yet."}
            {view === 'hire' && "No candidates have been hired yet. Complete interviews and select 'Hire' in feedback to see them here."}
            {view === 'assessments' && "No assessment interviews found."}
            {view === 'all' && "No interviews found matching your criteria."}
          </p>
          {/* {view !== 'hire' && (
            // <button
            //   className="int-schedule-first-btn"
            //   onClick={() => {
            //     const jobsTab = document.querySelector('.rd-tab-button');
            //     if (jobsTab) jobsTab.click();
            //   }}
            // >
            //   <span className="int-btn-iconss"></span>
            //   Schedule Your First Interview
            // </button>
          )} */}
        </div>
      )}

      {/* Offer Letter Popup */}
      {showOfferLetter && selectedCandidate && (
        <Suspense fallback={
          <div className="int-modal-overlay">
            <div className="int-modal-content">
              <div className="int-loading-spinner"></div>
              <p>Loading Offer Letter...</p>
            </div>
          </div>
        }>
          <OfferLetter
            application={selectedCandidate}
            onClose={handleCloseOfferLetter}
            onSave={handleOfferLetterSave}
            user={getCurrentUser()}
            API_BASE_URL={API_BASE_URL}
          />
        </Suspense>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && selectedInterview && (
        <div className="int-modal-overlay">
          <div className="int-modal-content int-feedback-modal">
            <div className="int-modal-header">
              <h3>Interview Feedback</h3>
              <p className="int-modal-subtitle">for {selectedInterview.candidateName}</p>
              <button
                className="int-close-btn"
                onClick={() => setShowFeedbackModal(false)}
              >
                ×
              </button>
            </div>

            <div className="int-modal-body">
              <div className="int-form-group">
                <label>Overall Rating</label>
                <div className="int-rating-stars">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      type="button"
                      className={`int-star-btn ${star <= feedback.rating ? 'int-star-active' : ''}`}
                      onClick={() => setFeedback(prev => ({ ...prev, rating: star }))}>
                      <FaStar />
                    </button>
                  ))}
                </div>
              </div>

              <div className="int-form-group">
                <label>Final Decision</label>
                <select
                  value={feedback.decision}
                  onChange={(e) => setFeedback(prev => ({ ...prev, decision: e.target.value }))}
                  className="int-decision-select"
                >
                  <option value="Move to Next Round">Move to Next Round</option>
                  <option value="Hire">Hire</option>
                  <option value="Reject">Reject</option>
                  <option value="On Hold">On Hold</option>
                </select>
              </div>

              <div className="int-form-group">
                <label>Strengths</label>
                {feedback.strengths.map((strength, index) => (
                  <div key={index} className="int-array-input-group">
                    <input
                      type="text"
                      placeholder="Enter strength..."
                      value={strength}
                      onChange={(e) => updateStrength(index, e.target.value)}
                      className="int-array-input"
                    />
                    {feedback.strengths.length > 1 && (
                      <button
                        type="button"
                        className="int-remove-item-btn"
                        onClick={() => removeStrength(index)}
                      >
                        −
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className="int-add-item-btn"
                  onClick={addStrength}
                >
                  + Add Strength
                </button>
              </div>

              <div className="int-form-group">
                <label>Areas for Improvement</label>
                {feedback.areasForImprovement.map((area, index) => (
                  <div key={index} className="int-array-input-group">
                    <input
                      type="text"
                      placeholder="Enter area for improvement..."
                      value={area}
                      onChange={(e) => updateAreaForImprovement(index, e.target.value)}
                      className="int-array-input"
                    />
                    {feedback.areasForImprovement.length > 1 && (
                      <button
                        type="button"
                        className="int-remove-item-btn"
                        onClick={() => removeAreaForImprovement(index)}
                      >
                        −
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className="int-add-item-btn"
                  onClick={addAreaForImprovement}
                >
                  + Add Area for Improvement
                </button>
              </div>

              <div className="int-form-group">
                <label>Additional Comments</label>
                <textarea
                  placeholder="Enter your overall comments about the candidate..."
                  value={feedback.comments}
                  onChange={(e) => setFeedback(prev => ({ ...prev, comments: e.target.value }))}
                  rows="4"
                  className="int-comments-textarea"
                />
              </div>
            </div>

            <div className="int-modal-actions">
              <button
                type="button"
                className="int-cancel-btn"
                onClick={() => setShowFeedbackModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="int-submit-btn"
                onClick={submitFeedback}
              >
                Submit Feedback
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Next Round Modal */}
      {showScheduleModal && selectedInterview && (
        <div className="int-modal-overlay">
          <div className="int-modal-content int-schedule-modal">
            <div className="int-modal-header">
              <h3>Schedule Next Round</h3>
              <p className="int-modal-subtitle">for {selectedInterview.candidateName}</p>
              <button
                className="int-close-btn"
                onClick={() => setShowScheduleModal(false)}
              >
                ×
              </button>
            </div>

            <div className="int-modal-body">
              <div className="int-form-row">
                <div className="int-form-group">
                  <label>Interview Date</label>
                  <input
                    type="date"
                    value={scheduleData.scheduledDate}
                    onChange={(e) => setScheduleData(prev => ({ ...prev, scheduledDate: e.target.value }))}
                    className="int-date-input"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div className="int-form-group">
                  <label>Interview Time</label>
                  <input
                    type="time"
                    value={scheduleData.scheduledTime}
                    onChange={(e) => setScheduleData(prev => ({ ...prev, scheduledTime: e.target.value }))}
                    className="int-time-input"
                  />
                </div>
              </div>

              <div className="int-form-row">
                <div className="int-form-group">
                  <label>Interview Type</label>
                  <select
                    value={scheduleData.interviewType}
                    onChange={(e) => setScheduleData(prev => ({ ...prev, interviewType: e.target.value }))}
                    className="int-type-select"
                  >
                    <option value="Technical">Technical</option>
                    <option value="HR">HR</option>
                    <option value="Managerial">Managerial</option>
                    <option value="Cultural Fit">Cultural Fit</option>
                    <option value="Final">Final</option>
                    <option value="Assessment">Assessment</option>
                  </select>
                </div>

                <div className="int-form-group">
                  <label>Interview Mode</label>
                  <select
                    value={scheduleData.interviewMode}
                    onChange={(e) => setScheduleData(prev => ({ ...prev, interviewMode: e.target.value }))}
                    className="int-mode-select"
                  >
                    <option value="Video Call">Video Call</option>
                    <option value="In-Person">In-Person</option>
                    <option value="Phone Call">Phone Call</option>
                  </select>
                </div>
              </div>

              {scheduleData.interviewMode === 'Video Call' && (
                <div className="int-form-group">
                  <label>Meeting Link</label>
                  <input
                    type="url"
                    placeholder="https://meet.google.com/xxx-xxxx-xxx"
                    value={scheduleData.meetingLink}
                    onChange={(e) => setScheduleData(prev => ({ ...prev, meetingLink: e.target.value }))}
                    className="int-link-input"
                  />
                </div>
              )}

              {scheduleData.interviewMode === 'In-Person' && (
                <div className="int-form-group">
                  <label>Location</label>
                  <input
                    type="text"
                    placeholder="Office address or meeting room"
                    value={scheduleData.location}
                    onChange={(e) => setScheduleData(prev => ({ ...prev, location: e.target.value }))}
                    className="int-location-input"
                  />
                </div>
              )}

              <div className="int-form-group">
                <label>Interviewers</label>
                {scheduleData.interviewers.map((interviewer, index) => (
                  <div key={index} className="int-array-input-group">
                    <input
                      type="text"
                      placeholder="Enter interviewer name..."
                      value={interviewer}
                      onChange={(e) => updateInterviewer(index, e.target.value)}
                      className="int-array-input"
                    />
                    {scheduleData.interviewers.length > 1 && (
                      <button
                        type="button"
                        className="int-remove-item-btn"
                        onClick={() => removeInterviewer(index)}
                      >
                        −
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className="int-add-item-btn"
                  onClick={addInterviewer}
                >
                  + Add Interviewer
                </button>
              </div>

              <div className="int-form-group">
                <label>Duration</label>
                <select
                  value={scheduleData.duration}
                  onChange={(e) => setScheduleData(prev => ({ ...prev, duration: e.target.value }))}
                  className="int-duration-select"
                >
                  <option value="30">30 minutes</option>
                  <option value="45">45 minutes</option>
                  <option value="60">60 minutes</option>
                  <option value="90">90 minutes</option>
                  <option value="120">120 minutes</option>
                </select>
              </div>

              <div className="int-form-group">
                <label>Notes</label>
                <textarea
                  placeholder="Additional notes for the interview..."
                  value={scheduleData.notes}
                  onChange={(e) => setScheduleData(prev => ({ ...prev, notes: e.target.value }))}
                  rows="3"
                  className="int-notes-textarea"
                />
              </div>
            </div>

            <div className="int-modal-actions">
              <button
                type="button"
                className="int-cancel-btn"
                onClick={() => setShowScheduleModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="int-submit-btn"
                onClick={scheduleNextRound}
                disabled={!scheduleData.scheduledDate || !scheduleData.scheduledTime}
              >
                Schedule Next Round
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Candidate Answers Modal */}
      {showAnswersModal && (
        <div className="int-modal-overlay">
          <div className="int-modal-content int-answers-modal">
            <div className="int-modal-header">
              <h3>Candidate Assessment Submissions</h3>
              <button
                className="int-close-btn"
                onClick={() => {
                  setShowAnswersModal(false);
                  setCandidateAnswers([]);
                }}
              >
                ×
              </button>
            </div>

            <div className="int-modal-body">
              {answersLoading ? (
                <div className="int-loading-state">
                  <div className="int-loading-spinner"></div>
                  <p>Loading candidate submissions...</p>
                </div>
              ) : candidateAnswers.length > 0 ? (
                <div className="int-submissions-list">
                  {candidateAnswers.map((submission) => (
                    <div key={submission._id} className="int-submission-card">
                      <div className="int-submission-header">
                        <h5>{submission.candidateName}</h5>
                        <span className={`int-score-badge ${submission.percentage >= 80 ? 'int-score-excellent' :
                            submission.percentage >= 60 ? 'int-score-good' :
                              submission.percentage >= 40 ? 'int-score-average' : 'int-score-poor'
                          }`}>
                          {submission.percentage}%
                        </span>
                      </div>
                      <p className="int-submission-email">{submission.candidateEmail}</p>

                      <div className="int-submission-meta">
                        <div className="int-meta-item">
                          <strong>Score:</strong>
                          <span>{submission.score}/{submission.totalQuestions}</span>
                        </div>
                        <div className="int-meta-item">
                          <strong>Time Spent:</strong>
                          <span>{formatTime(submission.timeSpent)}</span>
                        </div>
                      </div>

                      <div className="int-submission-actions">
                        <button
                          className="int-view-details-btn"
                          onClick={() => viewSubmissionDetails(submission._id)}
                          disabled={answersLoading}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="int-empty-submissions">
                  <div className="int-empty-icon">
                    <FaPen />
                  </div>
                  <h4>No Submissions Found</h4>
                  <p>No candidates have submitted this assessment yet.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Detailed Submission View Modal */}
      {showDetailedView && detailedSubmission && (
        <div className="int-modal-overlay">
          <div className="int-modal-content int-details-modal">
            <div className="int-modal-header">
              <h3>Submission Details</h3>
              <p className="int-modal-subtitle">{detailedSubmission.submission.candidateName}</p>
              <button
                className="int-close-btn"
                onClick={closeDetailedView}
              >
                ×
              </button>
            </div>

            <div className="int-modal-body">
              <div className="int-submission-summary">
                <div className="int-summary-card">
                  <h4>Assessment Summary</h4>
                  <div className="int-summary-grid">
                    <div className="int-summary-item">
                      <strong>Candidate:</strong>
                      <span>{detailedSubmission.submission.candidateName}</span>
                    </div>
                    <div className="int-summary-item">
                      <strong>Email:</strong>
                      <span>{detailedSubmission.submission.candidateEmail}</span>
                    </div>
                    <div className="int-summary-item">
                      <strong>Score:</strong>
                      <span className="int-score-display">
                        {detailedSubmission.submission.score}/{detailedSubmission.submission.totalQuestions}
                      </span>
                    </div>
                    <div className="int-summary-item">
                      <strong>Percentage:</strong>
                      <span className={`int-percentage-display ${detailedSubmission.submission.percentage >= 80 ? 'int-percentage-excellent' :
                          detailedSubmission.submission.percentage >= 60 ? 'int-percentage-good' :
                            detailedSubmission.submission.percentage >= 40 ? 'int-percentage-average' : 'int-percentage-poor'
                        }`}>
                        {detailedSubmission.submission.percentage}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="int-questions-section">
                <h4>Questions & Answers</h4>
                {detailedSubmission.questions && detailedSubmission.questions.map((question, index) => (
                  <div key={question._id} className="int-question-card">
                    <div className="int-question-header">
                      <h5>Question {index + 1}</h5>
                      <span className="int-question-type">{question.type}</span>
                    </div>

                    <div className="int-question-content">
                      <p className="int-question-text">{question.question}</p>

                      {question.type === 'multiple_choice' && question.options && (
                        <div className="int-options-list">
                          <strong>Options:</strong>
                          <ul>
                            {question.options.map((option, optIndex) => (
                              <li
                                key={optIndex}
                                className={`int-option-item ${option === detailedSubmission.submission.answers[question._id] ? 'int-option-selected' : ''
                                  } ${question.correctAnswer && option === question.correctAnswer ? 'int-option-correct' : ''
                                  }`}
                              >
                                {option}
                                {option === detailedSubmission.submission.answers[question._id] && ' ✓'}
                                {question.correctAnswer && option === question.correctAnswer && ' (Correct)'}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    <div className="int-answer-content">
                      <strong>Candidate's Answer:</strong>
                      {renderAnswerContent(question, detailedSubmission.submission.answers[question._id])}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="int-modal-actions">
              <button
                className="int-close-details-btn"
                onClick={closeDetailedView}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewsList;